import { DateTime } from "luxon";
import { MONTHS_OF_YEAR } from "../../constants";
import { circularArrayStep, getDaysInMonth } from "../helpers";

const withinSameDay = (start, end) => start.toObject().day === end.toObject().day;
const daysInRangeRemaining = (start, end) => Object.keys(start).some((k) => start[k] !== end[k]);
const endOfDay = { hour: 23, minute: 59, second: 59 };
const startOfDay = { hour: 0, minute: 0, seconds: 0 };

/**
 * @param {String} startISO
 * @param {String} endISO 
 * @param {String} timezone tz string
 * 
 * @return {Array.<{startTime: String, endTime: String}>}
 */
export default (startISO, endISO, timezone = "UTC") => {
  let startTime;
  let endTime;
  const start = DateTime.fromISO(startISO, { zone: timezone });
  const end = DateTime.fromISO(endISO, { zone: timezone });

  if (end < start) return [];

  // start and end times occur within same day
  if (withinSameDay(start, end)) {
    startTime = start.toISO();
    endTime = end.toISO();
    return [{ startTime, endTime }];
  };

  // if start and end spans multiple days
  const output = [];
  const monthsList = MONTHS_OF_YEAR.map((m) => m.toLowerCase());
  const endData = {
    day: end.toObject().day,
    month: end.toObject().month,
    year: end.toObject().year,
  };
  const currentData = {
    day: start.toObject().day,
    month: start.toObject().month,
    year: start.toObject().year,
  };
  let timeStamps = {
    // this is the first entry: natural startTime + endTime is set to EOD
    startTime: start.toISO(),
    endTime: end.set({ ...currentData, ...endOfDay }).toISO(),
  };
  let currentMonthName = monthsList[currentData.month - 1];
  
  // middle entries
  while (daysInRangeRemaining(currentData, endData)) {
    output.push(timeStamps);
    if (currentData.day + 1 <= getDaysInMonth(currentMonthName)) {
      // crosses midnight, but no month or year crossover
      currentData.day += 1;
    } else if (currentData.day + 1 > getDaysInMonth(currentMonthName) && currentData.month < 12) {
      // crosses midnight and month, but no year crossover
      currentData.day = 1;
      currentMonthName = circularArrayStep(monthsList, monthsList.indexOf(currentMonthName));
      currentData.month = monthsList.indexOf(currentMonthName) + 1;
    } else {
      // crosses midnight, month, AND year
      currentData.day = 1;
      currentData.month = 1;
      currentData.year += 1;
      currentMonthName = "january";
    }
    timeStamps = {
      // new timesStamps get pushed at top of the loop if condition not met
      startTime: start.set({ ...currentData, ...startOfDay }).toISO(),
      endTime: end.set({ ...currentData, ...endOfDay }).toISO(),
    };
  };

  // for final entry: endTime is set to natural endTime
  timeStamps.endTime = DateTime.fromISO(endISO, { zone: timezone }).toISO();
  output.push(timeStamps);

  return output;
};
